import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import "./addBuilder.css";
import { addNewBuilder } from "../../Redux/Actions/builderActions";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCityListPagination,
  viewCountryListPagination,
  viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const AddBuilder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { countryList } = useSelector((state) => state.country);
  const { states } = useSelector((state) => state.states);
  const { cities } = useSelector((state) => state.city);
  const { loading = false } = useSelector((state) => state.builder || {});

  const [activeSection, setActiveSection] = useState(0);
  const [confirmPass, setConfirmPass] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const [builderData, setBuilderData] = useState({
    primary_email: "",
    brand_name: "",
    organization_name: "",
    primary_country_code: "",
    primary_contact_number: "",
    secondary_country_code: "",
    secondary_contact_number: "",
    street_address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    rera_id: "",
    pwd: "",
    lat: "",
    lng: "",
  });

  const [stateData, setStateData] = useState({ country: "" });
  const [cityData, setCityData] = useState({ state: "", country: "" });

  useEffect(() => { 
    dispatch(viewCountryListPagination()); 
  }, [dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (stateData.country !== "") dispatch(viewStateListPagination(stateData));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [stateData.country, dispatch, stateData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (cityData.state !== "") dispatch(viewCityListPagination(cityData));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [cityData, cityData.state, dispatch]);

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setBuilderData((prev) => ({ ...prev, [name]: value }));
    
    if (name === "country") {
      setStateData({ country: value });
      setCityData((prev) => ({ ...prev, country: value, state: "" }));
      setBuilderData((prev) => ({ ...prev, state: "", city: "", country: value }));
      return;
    }
    if (name === "state") {
      setCityData((prev) => ({ ...prev, state: value }));
      setBuilderData((prev) => ({ ...prev, city: "", state: value }));
      return;
    }
  };

  const handleCheckPass = () => {
    if (confirmPass && builderData.pwd !== confirmPass) {
      toast.error("Passwords do not match");
    }
  };

  const sections = ["Basic Info", "Contact", "Security", "Address"];

  const handleNextSection = () => {
    const next = Math.min(sections.length - 1, activeSection + 1);
    if (next === 4 && !builderData.street_address.trim()) {
      toast.error("Street address is required before proceeding");
      return;
    }
    setActiveSection(next);
  };

  const handleReset = () => {
    setBuilderData({
      primary_email: "", brand_name: "", organization_name: "",
      primary_country_code: "", primary_contact_number: "",
      secondary_country_code: "", secondary_contact_number: "",
      street_address: "", city: "", state: "", country: "",
      zipcode: "", rera_id: "", pwd: "", lat: "", lng: "",
    });
    setConfirmPass("");
    setShowPwd(false);
    setShowConfirmPwd(false);
    setActiveSection(0);
    setStateData({ country: "" });
    setCityData({ state: "", country: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!builderData.brand_name.trim()) return toast.error("Brand name is required");
    if (!builderData.organization_name.trim()) return toast.error("Organization name is required");
    if (!builderData.primary_email.trim() || !builderData.primary_email.includes("@"))
      return toast.error("Valid primary email is required");
    if (!builderData.primary_country_code) return toast.error("Primary country code is required");
    if (!builderData.primary_contact_number.trim()) return toast.error("Primary contact number is required");
    if (!builderData.pwd) return toast.error("Password is required");
    if (builderData.pwd.length < 8) return toast.error("Password must be at least 8 characters");
    if (!confirmPass) return toast.error("Confirm password is required");
    if (builderData.pwd !== confirmPass) return toast.error("Passwords do not match");
    if (!builderData.street_address.trim()) return toast.error("Street address is required");
    if (!builderData.country) return toast.error("Country is required");
    if (!builderData.state) return toast.error("State is required");
    if (!builderData.city) return toast.error("City is required");
    if (!builderData.zipcode.trim()) return toast.error("Zip code is required");

    try {
      const response = await dispatch(addNewBuilder(builderData));
      if (response?.statusCode === 200) {
        toast.success("Builder added successfully!");
        handleReset();
        navigate("/builders");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add builder");
    }
  };

  return (
    <MainLayout>
      <div className="ab-wrapper">
        <div className="ab-header">
          <div className="ab-header-left">
            <div className="ab-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </div>
            <div>
              <h1 className="ab-title">Add Builder</h1>
              <p className="ab-subtitle">Fill in the details to onboard a new builder</p>
            </div>
          </div>
          <Link to="/builders" className="ab-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
            View Builders
          </Link>
        </div>

        <div className="ab-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`ab-nav-pill ${activeSection === i ? "ab-nav-pill--active" : ""}`}
              onClick={() => {
                if (i === 4 && !builderData.street_address.trim()) {
                  toast.error("Street address is required");
                  return;
                }
                setActiveSection(i);
              }}
              type="button"
              disabled={loading}
            >
              <span className="ab-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="ab-form">
          <div className={`ab-section ${activeSection === 0 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">01</span>
              <h2 className="ab-section-title">Basic Information</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <div className="ab-field">
                <label className="ab-label">Brand Name <span className="ab-req">*</span></label>
                <input className="ab-input" type="text" name="brand_name" value={builderData.brand_name} onChange={handleAddChange} placeholder="e.g. Sunrise Builders" disabled={loading} />
              </div>
              <div className="ab-field">
                <label className="ab-label">Organization Name <span className="ab-req">*</span></label>
                <input className="ab-input" type="text" name="organization_name" value={builderData.organization_name} onChange={handleAddChange} placeholder="e.g. Sunrise Real Estate Pvt Ltd" disabled={loading} />
              </div>
              <div className="ab-field">
                <label className="ab-label">RERA ID <span className="ab-optional">(Optional)</span></label>
                <input className="ab-input" type="text" name="rera_id" value={builderData.rera_id} onChange={handleAddChange} placeholder="e.g. PRR123456" disabled={loading} />
              </div>
            </div>
          </div>

          <div className={`ab-section ${activeSection === 1 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">02</span>
              <h2 className="ab-section-title">Contact Details</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <div className="ab-field ab-field--full">
                <label className="ab-label">Primary Email <span className="ab-req">*</span></label>
                <input className="ab-input" type="email" name="primary_email" value={builderData.primary_email} onChange={handleAddChange} placeholder="builder@example.com" disabled={loading} />
              </div>
              <div className="ab-field">
                <label className="ab-label">Primary Country Code <span className="ab-req">*</span></label>
                <select className="ab-select" name="primary_country_code" value={builderData.primary_country_code} onChange={handleAddChange} disabled={loading}>
                  <option value="">Select country code</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country.country_phone_code}>
                      {country.country_phone_code} — {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-label">Primary Contact Number <span className="ab-req">*</span></label>
                <input className="ab-input" type="tel" name="primary_contact_number" value={builderData.primary_contact_number} onChange={handleAddChange} placeholder="Enter primary phone number" disabled={loading} />
              </div>
              <div className="ab-field">
                <label className="ab-label">Secondary Country Code <span className="ab-optional">(Optional)</span></label>
                <select className="ab-select" name="secondary_country_code" value={builderData.secondary_country_code} onChange={handleAddChange} disabled={loading}>
                  <option value="">Select country code</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country.country_phone_code}>
                      {country.country_phone_code} — {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-label">Secondary Contact Number <span className="ab-optional">(Optional)</span></label>
                <input className="ab-input" type="tel" name="secondary_contact_number" value={builderData.secondary_contact_number} onChange={handleAddChange} placeholder="Enter secondary phone number" disabled={loading} />
              </div>
            </div>
          </div>

          <div className={`ab-section ${activeSection === 2 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">03</span>
              <h2 className="ab-section-title">Security</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <div className="ab-field">
                <label className="ab-label">Password <span className="ab-req">*</span></label>
                <div className="ab-input-wrap">
                  <input
                    className="ab-input ab-input--icon"
                    type={showPwd ? "text" : "password"}
                    name="pwd"
                    value={builderData.pwd}
                    onChange={handleAddChange}
                    placeholder="Create a strong password"
                    disabled={loading}
                  />
                  <button type="button" className="ab-eye-btn" onClick={() => setShowPwd((v) => !v)} tabIndex={-1} disabled={loading}>
                    {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <div className="ab-field">
                <label className="ab-label">Confirm Password <span className="ab-req">*</span></label>
                <div className="ab-input-wrap">
                  <input
                    className="ab-input ab-input--icon"
                    type={showConfirmPwd ? "text" : "password"}
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                    onBlur={handleCheckPass}
                    placeholder="Re-enter password"
                    disabled={loading}
                  />
                  <button type="button" className="ab-eye-btn" onClick={() => setShowConfirmPwd((v) => !v)} tabIndex={-1} disabled={loading}>
                    {showConfirmPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
            </div>
            <div className="ab-pass-rules">
              <p className="ab-pass-rule-title">Password requirements:</p>
              <ul className="ab-pass-rule-list">
                <li>At least 8 characters long</li>
                {/* <li>Contains uppercase and lowercase letters</li> */}
                <li>Includes at least one number</li>
              </ul>
            </div>
          </div>

          <div className={`ab-section ${activeSection === 3 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">04</span>
              <h2 className="ab-section-title">Address Details</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <div className="ab-field ab-field--full">
                <label className="ab-label">Street Address <span className="ab-req">*</span></label>
                <input className="ab-input" type="text" name="street_address" value={builderData.street_address} onChange={handleAddChange} placeholder="Street, building, apartment…" disabled={loading} />
              </div>
              <div className="ab-field">
                <label className="ab-label">Country <span className="ab-req">*</span></label>
                <select className="ab-select" name="country" value={builderData.country} onChange={handleAddChange} disabled={loading}>
                  <option value="">Select country</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country._id}>{country.country_name}</option>
                  ))}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-label">State <span className="ab-req">*</span></label>
                <select
                  className={`ab-select ${!builderData.country ? "ab-select--disabled" : ""}`}
                  name="state"
                  value={builderData.state}
                  onChange={handleAddChange}
                  disabled={!builderData.country || loading}
                >
                  <option value="">{!builderData.country ? "Select country first" : "Select state"}</option>
                  {states?.data?.map((state) => (
                    <option key={state._id} value={state._id}>{state.state_name}</option>
                  ))}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-label">City <span className="ab-req">*</span></label>
                <select
                  className={`ab-select ${!builderData.state ? "ab-select--disabled" : ""}`}
                  name="city"
                  value={builderData.city}
                  onChange={handleAddChange}
                  disabled={!builderData.state || loading}
                >
                  <option value="">{!builderData.state ? "Select state first" : "Select city"}</option>
                  {cities?.data?.map((city) => (
                    <option key={city._id} value={city._id}>{city.city_name}</option>
                  ))}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-label">Zip / Postal Code <span className="ab-req">*</span></label>
                <input className="ab-input" type="text" name="zipcode" value={builderData.zipcode} onChange={handleAddChange} placeholder="e.g. 560001" disabled={loading} />
              </div>
            </div>
          </div>

          <div className="ab-footer">
            <div className="ab-footer-nav">
              <button
                type="button"
                className="ab-btn ab-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                Back
              </button>
              {activeSection < sections.length - 1 ? (
                <button type="button" className="ab-btn ab-btn--primary" onClick={handleNextSection} disabled={loading}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="ab-btn ab-btn--submit" disabled={loading}>
                  {loading ? "Saving..." : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                      Save Builder
                    </>
                  )}
                </button>
              )}
            </div>
            <Link to="/builders" className="ab-btn ab-btn--cancel" onClick={handleReset}>Cancel</Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddBuilder;
